import useAiToolStateReducer from './useAiToolStateReducer';

import { AiConversation, AIResponseElement, AiDebugInfo } from '../../../../../../../common/ai/aiConversationTypes';
import { AIConversationResultType } from './aiToolStateConstants';
import { PromptShortcut } from '../../../../../../../common/ai/aiPromptShortcutTypes';

export const isIdeaResponse = (response: AIResponseElement | AiHistoryEntry): response is AIResponseElement =>
    response.elementType !== AIConversationResultType.PROMPT_HISTORY &&
    response.elementType !== AIConversationResultType.BOT_HISTORY &&
    response.elementType !== AIConversationResultType.DEBUG_INFO;
export const isIdeaPromptHistory = (response: AIResponseElement | AiHistoryEntry): response is AIResponseElement =>
    response.elementType === AIConversationResultType.PROMPT_HISTORY;
export const isIdeaDebugInfo = (response: AIResponseElement | AiHistoryEntry): response is AIResponseElement =>
    response.elementType === AIConversationResultType.DEBUG_INFO;
export const isIdeaBotHistory = (response: AIResponseElement | AiHistoryEntry): response is AIResponseElement =>
    response.elementType === AIConversationResultType.BOT_HISTORY;

export enum AiPromptMode {
    HISTORY = 'HISTORY',
    PROMPTS = 'PROMPTS',
}

export enum AiPopupTransitionDirection {
    UP = 'up',
    DOWN = 'down',
}

export type AiHistoryEntry = Partial<{
    uuid: string;
    elementType: string;
    text: string;
}>;

export type AiResultType = AIResponseElement | AiHistoryEntry | AiDebugInfo;

export type AiLocalAiConversation = Partial<AiConversation> & {
    _id: string;
    fetching: boolean;
    fetched: boolean;
    streamingResults: boolean;
    streamingFollowupPrompts: boolean;
    title: string;
    error: string | null;
    results: AiResultType[];
    selectedPromptShortcuts: PromptShortcut[];
    previousSelectedPromptShortcuts: PromptShortcut[];
};

export enum AiToolActionType {
    SET_MODE = 'SET_MODE',
    SET_RETURN_MODE = 'SET_RETURN_MODE',
    SET_TRANSITION_DIRECTION = 'SET_TRANSITION_DIRECTION',
    SET_CURRENT_CONVERSATION_ID = 'SET_CURRENT_CONVERSATION_ID',
    UPDATE_CONVERSATION = 'UPDATE_CONVERSATION',
    LOAD_CONVERSATION = 'LOAD_CONVERSATION',
    LOAD_CONVERSATION_STUBS = 'LOAD_CONVERSATION_STUBS',
    CREATE_CONVERSATION = 'CREATE_CONVERSATION',
    ADD_CONVERSATION_RESULTS = 'ADD_CONVERSATION_RESULTS',
    SET_CONVERSATION_PROMPT_SHORTCUTS = 'SET_CONVERSATION_PROMPT_SHORTCUTS',
    RESET_CONVERSATION_PROMPT_SHORTCUTS = 'RESET_CONVERSATION_PROMPT_SHORTCUTS',
}

export type AiToolAction =
    | {
          type: AiToolActionType.SET_MODE;
          payload: AiPromptMode;
      }
    | {
          type: AiToolActionType.SET_RETURN_MODE;
          payload: AiPromptMode;
      }
    | {
          type: AiToolActionType.SET_TRANSITION_DIRECTION;
          payload: AiPopupTransitionDirection;
      }
    | {
          type: AiToolActionType.SET_CURRENT_CONVERSATION_ID;
          payload: string;
      }
    | {
          type: AiToolActionType.UPDATE_CONVERSATION;
          payload: {
              conversationId: string;
              updates: Partial<AiLocalAiConversation>;
          };
      }
    | {
          type: AiToolActionType.LOAD_CONVERSATION;
          payload: {
              conversationId: string;
              conversation: AiConversation;
          };
      }
    | {
          type: AiToolActionType.LOAD_CONVERSATION_STUBS;
          payload: {
              conversations: Partial<AiConversation>[];
          };
      }
    | {
          type: AiToolActionType.CREATE_CONVERSATION;
          payload: {
              conversationId: string;
              rootPromptShortcut: PromptShortcut | undefined;
          };
      }
    | {
          type: AiToolActionType.ADD_CONVERSATION_RESULTS;
          payload: {
              conversationId: string;
              results: AiResultType[];
          };
      }
    | {
          type: AiToolActionType.SET_CONVERSATION_PROMPT_SHORTCUTS;
          payload: {
              conversationId: string;
              promptShortcuts: PromptShortcut[];
          };
      }
    | {
          type: AiToolActionType.RESET_CONVERSATION_PROMPT_SHORTCUTS;
          payload: {
              conversationId: string;
              rootPromptShortcut: PromptShortcut | undefined;
          };
      };

export interface AiToolState {
    currentConversationId: string;
    conversations: {
        [conversationId: string]: AiLocalAiConversation;
    };
    mode: AiPromptMode;
    returnMode: AiPromptMode;
    transitionDirection: AiPopupTransitionDirection;
}

export type UseAiToolState = ReturnType<typeof useAiToolStateReducer>;
